import type { Router, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { RightName } from '@/config/nav'
import { i18n } from '@/i18n'

let attemptedRestore = false

export function resetSessionRestoreFlag() {
    attemptedRestore = false
}

const BRAND = 'Bzuco Inspector'

// Map route name → i18n key. Fall back to `nav.<name>` for routes that
// follow the convention.
const TITLE_KEY_BY_ROUTE: Record<string, string> = {
    login: 'nav.login',
    dashboard: 'nav.dashboard',
    events: 'nav.events',
    ticketsets: 'nav.ticketsets',
    tickets: 'nav.tickets',
    'tickets-import': 'tickets.import.title',
    'tickets-verify': 'tickets.verify.title',
    devices: 'nav.devices',
    statistics: 'nav.statistics',
    users: 'nav.users',
    help: 'nav.help',
    'not-found': 'common.notFound'
}

function titleFor(to: RouteLocationNormalized): string {
    const metaKey = to.meta?.titleKey as string | undefined
    const key = metaKey ?? TITLE_KEY_BY_ROUTE[String(to.name ?? '')]
    if (!key) return BRAND
    try {
        const label = i18n.global.t(key)
        if (label && label !== key) return `${label} · ${BRAND}`
    } catch {
        // missing key — fall through
    }
    return BRAND
}

export function installGuards(router: Router) {
    router.beforeEach(async to => {
        const auth = useAuthStore()

        if (!auth.isAuthenticated && !attemptedRestore) {
            attemptedRestore = true
            try {
                await auth.fetchCurrentUser()
            } catch {
                // not logged in — guard handles it below
            }
        }

        if (to.meta.requiresAuth && !auth.isAuthenticated) {
            if (to.name === 'login') return true
            return { name: 'login', query: { next: to.fullPath } }
        }

        const required = to.meta.requiredRight as RightName | undefined
        if (required && !auth.hasRight(required)) {
            return { name: 'dashboard' }
        }

        return true
    })

    router.afterEach(to => {
        document.title = titleFor(to)
    })
}
